import { checkIfIdIsValidForDecoding } from 'mini-mail-bootstrap/lib/routing/mailViewHandler';
import { logUsage } from 'owa-analytics';

export const BASE64_KEY_STRING =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=';

/**
 * Decodes the specified base64-encoded input
 * @param input base64-encoded input
 * @returns a binary string
 */
//The function is from client-web, but is not currently importable via a shared packaged. For the source code, please refer https://outlookweb.visualstudio.com/Outlook%20Web/_git/client-web?path=/packages/mail/packages/controls/owa-encoding-utils/src/utils/base64/decode.ts&version=GBmaster&line=8&lineEnd=67&lineStartColumn=1&lineEndColumn=2&lineStyle=plain&_a=contents
function decode(input: string): string {
    if (!input) {
        return input;
    }
    const firstCharCode = input.toLowerCase().charCodeAt(0);
    if (
        firstCharCode >= 97 &&
        firstCharCode <= 122 &&
        input.indexOf('#') === -1 &&
        input.indexOf('-') === -1
    ) {
        try {
            return atob(input);
        } catch (e) {
            // Browser's built in base64 decoder didn't work, use our custom one
        }
    }

    let output = '';
    let i = 0;

    // The function to get next char skipping \r and \n
    // We remove these characters because some base64 encoders inserts \r\n to the output string every 76 characters.
    // Per this article http://en.wikipedia.org/wiki/Base64, \r\n is used as a line separator for MIME (RFC 2045), standard base64 (RFC 3548 & 4648).
    // Strict mode was enabled in this package. See aka.ms/client-web-strict-mode for details.
    // -> Error TS7023 (35,11): 'next' implicitly has return type 'any' because it does not have a return type annotation and is referenced directly or indirectly in one of its return expressions.
    const next = () => {
        const chr = input.charAt(i++);
        switch (chr) {
            case '\r':
            case '\n':
                return next();
            default:
                return chr;
        }
    };

    while (i < input.length) {
        const encoding1 = BASE64_KEY_STRING.indexOf(next());
        const encoding2 = BASE64_KEY_STRING.indexOf(next());
        const encoding3 = BASE64_KEY_STRING.indexOf(next());
        const encoding4 = BASE64_KEY_STRING.indexOf(next());

        const chr1 = (encoding1 << 2) | (encoding2 >> 4);
        const chr2 = ((encoding2 & 15) << 4) | (encoding3 >> 2);
        const chr3 = ((encoding3 & 3) << 6) | encoding4;

        output += String.fromCharCode(chr1);

        if (encoding3 != 64) {
            output += String.fromCharCode(chr2);
        }

        if (encoding4 != 64) {
            output += String.fromCharCode(chr3);
        }
    }
    return output;
}

function substringToLowerCase(idParam: string, startIndex, endIndex) {
    const subString = idParam.substring(startIndex + 1, endIndex);
    idParam = idParam.replace(subString, subString.toLowerCase());
    return idParam;
}

export default function convertWin32IdToOutlookId(idParam: string) {
    try {
        let id = idParam;
        const charactersToCheckFor = ['%3d', '%2f', '%2b', '%3D', '%2F', '%2B'];
        const shouldDecode = charactersToCheckFor.some(char => id.indexOf(char) >= 0);
        if (shouldDecode) {
            id = decodeURIComponent(id);
        }
        const base64Id = decode(id);
        const lowerCaseMailboxID = substringToLowerCase(
            base64Id,
            base64Id.indexOf('{'),
            base64Id.indexOf('}')
        );
        const removeLeftBracket = lowerCaseMailboxID.replace('{', '');
        const removeAllBrackets = removeLeftBracket.replace('}', '');
        const replaceAndSign = removeAllBrackets.replace('&', '$');

        //convert back
        const finalId = btoa(replaceAndSign);
        return finalId;
    } catch (err) {
        return idParam;
    }
}

function convertBase64ToHexa(id: string, type: IDType) {
    if (checkIfIdIsValidForDecoding(id, type)) {
        const buffer = Buffer.from(id, 'base64');
        const bufString = buffer.toString('hex');
        return bufString;
    }
    logUsage('nonDecodableWin32ID', { id: id, idT: type }, { isCore: true });
    try {
        return convertBinarytoHex(
            backUpDecoding(
                id +
                    'A' /*Conversion strips last character, so adding an unnecessary last character to get the exact value*/
            )
        );
    } catch (e) {
        logUsage('backUpDecodingError', { id: id, idT: type }, { isCore: true });
        return id;
    }
}

export function extractStoreID(id: string, type: IDType) {
    const convertedId = convertBase64ToHexa(id, type);
    logUsage('convertedStoreID', { id: convertedId }, { isCore: true });
    if (type == 1) {
        const startIndexForStoreId = convertedId.indexOf('7d00');
        return convertedId.slice(startIndexForStoreId + 8);
    } else {
        const startIndexForStoreId = convertedId.indexOf('001000');
        const slicedId = convertedId.slice(startIndexForStoreId + 6);
        if (slicedId.length != 32) {
            logUsage('IncorrectStoreId', { id: slicedId }, { isCore: true });
        }
        return slicedId;
    }
}

enum IDType {
    id,
    itemId,
}

//Strip everything except the mailbox ID. Win32 ID is then removed of braces and made lower case to match Outlook on Shoreline's mailbox Id.
export function extractMailBoxGUID(id: string, type: IDType) {
    const convertedId = convertBase64ToHexa(id, type);
    let initialMailBoxGUID;
    if (type == 0) {
        initialMailBoxGUID = convertedId.slice(
            10,
            convertedId.length -
                extractStoreID(id, type).length -
                8 /*8 for the 1 processing byte, 2 length bytes and the } bracket byte*/
        ); //This set of characters will always give the mailbox guid, though the value may need to be expanded. Please refer https://microsoft.sharepoint.com/teams/peoplehub/_layouts/15/Doc.aspx?sourcedoc={35f5025d-cd6f-42ac-af1a-8450146a2eb9}&action=view&wd=target%28Decoding%20IDs%7Cb460d7e7-b7b4-44db-a971-2847e11c3eb2%2F%29&wdorigin=NavigationUrl
    } else if (type == 1) {
        const endingBracePosition = convertedId.indexOf('7d');
        initialMailBoxGUID = convertedId.slice(10, endingBracePosition); //Refer https://office.visualstudio.com/Office/_git/Office?path=/outlook/sharedlibs/utils/ewsidconverterbase.cpp&_a=contents&version=GBmain - the Win32 code for ItemIds
    }
    if (initialMailBoxGUID.endsWith('7d')) {
        initialMailBoxGUID = initialMailBoxGUID.slice(0, initialMailBoxGUID.length - 2);
    }
    let convertedMailBoxGUID = hexToASCII(initialMailBoxGUID);
    const lowerCaseGUID = convertedMailBoxGUID.toLowerCase();
    const finalGUID = stringToHex(lowerCaseGUID);
    logUsage('convertedMailboxGUID', { id: finalGUID }, { isCore: true });
    return finalGUID;
}

/*
function expandMailBoxGUID(initialMailBoxGUID: string) {
    const characterPairs = [];
    for (let i = 0; i < initialMailBoxGUID.length; i += 2) {
        characterPairs.push(initialMailBoxGUID[i] + initialMailBoxGUID[i + 1]);
    }
    let finalGUID = '';
    for (let i = 0; i < characterPairs.length; ) {
        if (i + 2 < characterPairs.length) {
            if (characterPairs[i] == characterPairs[i + 1]) {
                finalGUID = finalGUID + characterPairs[i] + characterPairs[i];
                for (let j = 0; j < parseInt(characterPairs[i + 2], 16); j++) {
                    finalGUID = finalGUID + characterPairs[i];
                }
                i = i + 3;
            } else {
                finalGUID = finalGUID + characterPairs[i];
                i += 1;
            }
        } else {
            finalGUID = finalGUID + characterPairs[i];
            i += 1;
        }
    }
    logUsage('expandedMailboxGUID', { id: finalGUID }, { isCore: true });
    return finalGUID;
}

function convertToTwoBytes(value: string) {
    if (value.length >= 4) {
        return value;
    }
    return value + '0'.repeat(4 - value.length);
}
*/

function backUpDecoding(str: string) {
    return Buffer.from(str, 'base64').toString('binary');
}

function convertBinarytoHex(id: string) {
    let hex = '';
    for (let i = 0; i < id.length; i++) {
        let str = id.charCodeAt(i).toString(16);
        hex += str.length === 1 ? '0' + str : str;
    }
    return hex;
}

export function generateId(id: string) {
    try {
        const type = IDType.id;
        const convertedId = convertBase64ToHexa(id, type);
        if (convertedId === id) {
            return id; //Both decoding attempts have failed
        }
        let genId = convertedId.slice(0, 4); //First 2 bytes will be the same as Win32
        if (id.length == 80 && genId.startsWith('00') /*Uncompressed ID*/) {
            //2 bytes for processing, 2 bytes for mailbox length, 36 bytes for mailboxGUID, 1 more processing byte, 2 bytes for length of storeID, 16 bytes for storeID  - totals to 57 bytes for the uncompressed MailboxGUID scenario. Upon decoding, this will always be 80
            return id;
        }
        const mailboxLengthCheck = convertedId.slice(4, 8);
        if (mailboxLengthCheck !== '2400' && mailboxLengthCheck !== '2600') {
            logUsage('InvalidMailboxGUIDReceived', { id: id }, { isCore: true });
        }
        const storeId = extractStoreID(id, type);
        const mailboxGUID = extractMailBoxGUID(id, type);
        const lengthOfMailboxGUID = '2400';
        genId = genId + lengthOfMailboxGUID + mailboxGUID; //This will always be 2400 for miniOWA. The expansion (refer commented code) fails for rare cases in premium consumer. Hard coding is safer.
        genId += '00'; //Mandatory processing byte. Is always 00.
        genId = genId + '1000' + storeId;
        genId = Buffer.from(genId, 'hex').toString('base64');
        logUsage('changedConvId', { id: genId }, { isCore: true });
        return genId;
    } catch (err) {
        logUsage('IDGenerationFailed', { id: id }, { isCore: true });
        return id;
    }
}

export function generateItemId(id: string) {
    try {
        const type = IDType.itemId;
        if (!checkIfIdIsValidForDecoding(id, type)) {
            return id;
        }
        const convertedId = convertBase64ToHexa(id, type);
        const mailboxLengthCheck = convertedId.slice(4, 8);
        if (mailboxLengthCheck !== '2400' && mailboxLengthCheck !== '2600') {
            logUsage('InvalidMailboxGUIDReceived', { id: id }, { isCore: true });
        }
        if (mailboxLengthCheck === '2400') {
            //Have received an itemId valid for Outlook, no changes needed
            return id;
        }
        let genId = convertedId.slice(0, 4); //First 2 bytes will be the same as Win32
        const mailboxGUID = extractMailBoxGUID(id, type);
        genId = genId + '2400' + mailboxGUID;
        const endingBracePosition = convertedId.indexOf('7d');
        genId = genId + convertedId.slice(endingBracePosition + 2);
        genId = Buffer.from(genId, 'hex').toString('base64');
        logUsage('changedItemId', { id: genId }, { isCore: true });
        return genId;
    } catch (err) {
        logUsage('ItemIdGenerationFailed', { id: id }, { isCore: true });
        return id;
    }
}

function hexToASCII(hexString) {
    return hexString
        .match(/[\s\S]{1,2}/g)
        .map(byte => String.fromCharCode(parseInt(byte, 16)))
        .join('');
}

function stringToHex(string) {
    let hexadecimalValue = '';
    string.split('').map(function (char) {
        if (char.charCodeAt(0).toString(16).length === 1) {
            hexadecimalValue += '0';
        }
        hexadecimalValue += char.charCodeAt(0).toString(16);
    });
    return hexadecimalValue;
}
